/**
 * FILE: packages/supabase/src/services/outreachFollowups.ts
 * PURPOSE: Community link outreach tracking — support follow-ups and engagement.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: Service layer for community_link_outreach + onboarding_email_log. Log activity on writes.
 * LAST_REVIEWED: 2026-05-23
 */


import { ServiceError } from '../internal/errors'
import { logActivity, withActivityActor, type ActivityActor } from '../internal/activity'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import { callInternalEdgeFunction } from "../internal/edgeFunctionAuth";

export type OutreachStatus =
  | 'pending'
  | 'email_sent'
  | 'engaged'
  | 'support_flagged'
  | 'support_contacted'
  | 'resolved'

export interface CommunityLinkOutreach {
  id: string
  student_id: string | null
  student_phone: string
  student_email: string | null
  student_name: string | null
  community_link_id: string | null
  link_type: 'attempt' | 'placement'
  link_url: string
  course: string | null
  attempt: string | null
  exp_band: string | null
  email_sent_at: string | null
  email_status: string | null
  resend_message_id: string | null
  reminder_email_sent_at: string | null
  reminder_email_status: string | null
  student_said_hi_at: string | null
  email_opened_at: string | null
  link_clicked_at: string | null
  support_flagged_at: string | null
  support_resolved_at: string | null
  support_contacted_at: string | null
  support_notes: string | null
  support_assigned_to: string | null
  status: OutreachStatus
  next_check_at: string | null
  check_count: number | null
  created_at: string | null
  updated_at: string | null
}

export interface OnboardingEmailLog {
  id: string
  student_phone: string
  student_email: string | null
  course: string | null
  email_subject: string | null
  email_body_html: string | null
  status: string | null
  outreach_status: OutreachStatus
  outreach_id: string | null
  sent_at: string | null
  email_opened_at: string | null
  link_clicked_at: string | null
  student_said_hi_at: string | null
  bot_links_sent_at: string | null
  follow_up_email_sent_at: string | null
  follow_up_email_status: string | null
  follow_up_outreach_id: string | null
  follow_up_resend_message_id: string | null
  follow_up_link_clicked_at: string | null
  follow_up_email_opened_at: string | null
  error: string | null
  resend_message_id: string | null
  support_flagged_at: string | null
  support_resolved_at: string | null
  support_contacted_at: string | null
  support_notes: string | null
  support_assigned_to: string | null
  next_check_at: string | null
  check_count: number | null
  created_at: string | null
  updated_at: string | null
  student?: { name: string | null; attempt: string | null }
}

export interface OutreachListFilters {
  status?: OutreachStatus | OutreachStatus[]
  course?: string
  linkType?: 'attempt' | 'placement'
  search?: string
  scenario?: string
}

/* ------------------------------------------------------------------ */
/* Community link outreach                                             */
/* ------------------------------------------------------------------ */

export async function getOutreachFollowups(
  filters: OutreachListFilters,
  page: number,
  pageSize: number = 50, client: DbClient = defaultDbClient
): Promise<{ data: CommunityLinkOutreach[]; count: number }> {
  let q = client
    .from('community_link_outreach')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1)

  if (filters.status) {
    if (Array.isArray(filters.status)) {
      q = q.in('status', filters.status)
    } else {
      q = q.eq('status', filters.status)
    }
  }
  if (filters.course) q = q.eq('course', filters.course)
  if (filters.linkType) q = q.eq('link_type', filters.linkType)
  if (filters.search) {
    q = q.or(
      `student_name.ilike.%${filters.search}%,student_phone.ilike.%${filters.search}%`
    )
  }

  const { data, error, count } = await q
  if (error) throw new ServiceError('Failed to fetch outreach followups', 'DB_ERROR', error)
  return { data: (data ?? []) as CommunityLinkOutreach[], count: count ?? 0 }
}

export async function getOutreachStats(client: DbClient = defaultDbClient): Promise<Record<string, number>> {
  const { data, error } = await client.from('community_link_outreach').select('status')

  if (error) throw new ServiceError('Failed to fetch outreach stats', 'DB_ERROR', error)

  const stats: Record<string, number> = {
    pending: 0,
    email_sent: 0,
    engaged: 0,
    support_flagged: 0,
    support_contacted: 0,
    resolved: 0,
    total: 0,
  }

  for (const row of data ?? []) {
    const s = row.status as string
    if (s in stats) stats[s] = (stats[s] ?? 0) + 1
    stats.total = (stats.total ?? 0) + 1
  }

  return stats
}

export async function markSupportContacted(
  id: string,
  notes: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('community_link_outreach')
    .update({
      status: 'support_contacted',
      support_contacted_at: new Date().toISOString(),
      support_notes: notes,
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to mark as contacted', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'outreach_support_contacted',
      entityType: 'community_link_outreach',
      entityId: id,
      newValues: { notes, status: 'support_contacted' },
    }), client
  )
}

export async function markSupportResolved(
  id: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('community_link_outreach')
    .update({
      status: 'resolved',
      support_resolved_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to mark as resolved', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'outreach_resolved',
      entityType: 'community_link_outreach',
      entityId: id,
      newValues: { status: 'resolved' },
    }), client
  )
}

export async function markSupportEngaged(
  id: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('community_link_outreach')
    .update({
      status: 'engaged',
      support_resolved_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to mark as engaged', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'outreach_engaged',
      entityType: 'community_link_outreach',
      entityId: id,
      newValues: { status: 'engaged' },
    }), client
  )
}

export async function markSupportFlagged(
  id: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('community_link_outreach')
    .update({
      status: 'support_flagged',
      support_flagged_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to flag for support', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'outreach_flagged',
      entityType: 'community_link_outreach',
      entityId: id,
      newValues: { status: 'support_flagged' },
    }), client
  )
}

/* ------------------------------------------------------------------ */
/* Onboarding email log                                                */
/* ------------------------------------------------------------------ */

export async function getOnboardingFollowups(
  filters: OutreachListFilters,
  page: number,
  pageSize: number = 50, client: DbClient = defaultDbClient
): Promise<{ data: OnboardingEmailLog[]; count: number }> {
  let q = client
    .from('onboarding_email_log')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1)

  if (filters.status) {
    if (Array.isArray(filters.status)) {
      q = q.in('outreach_status', filters.status)
    } else {
      q = q.eq('outreach_status', filters.status)
    }
  }
  if (filters.course) q = q.eq('course', filters.course)
  if (filters.search) {
    q = q.ilike('student_phone', `%${filters.search}%`)
  }

  const { data, error, count } = await q
  if (error) throw new ServiceError('Failed to fetch onboarding followups', 'DB_ERROR', error)

  const logs = (data ?? []) as OnboardingEmailLog[]

  /* Fetch student names separately — FK missing so embedded join fails */
  if (logs.length > 0) {
    const phones = [...new Set(logs.map((l) => l.student_phone))]
    const { data: students } = await client
      .from('students')
      .select('phone, name, attempt')
      .in('phone', phones)
    const byPhone = new Map(students?.map((s) => [s.phone, s]) ?? [])
    for (const log of logs) {
      const s = byPhone.get(log.student_phone)
      if (s) {
        log.student = { name: s.name, attempt: s.attempt }
      }
    }
  }

  return { data: logs, count: count ?? 0 }
}

export async function getOnboardingStats(client: DbClient = defaultDbClient): Promise<Record<string, number>> {
  const { data, error } = await client
    .from('onboarding_email_log')
    .select('outreach_status')

  if (error) throw new ServiceError('Failed to fetch onboarding stats', 'DB_ERROR', error)

  const stats: Record<string, number> = {
    pending: 0,
    email_sent: 0,
    engaged: 0,
    support_flagged: 0,
    support_contacted: 0,
    resolved: 0,
    total: 0,
  }

  for (const row of data ?? []) {
    const s = row.outreach_status as string
    if (s in stats) stats[s] = (stats[s] ?? 0) + 1
    stats.total = (stats.total ?? 0) + 1
  }

  return stats
}

export async function markOnboardingSupportContacted(
  id: string,
  notes: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('onboarding_email_log')
    .update({
      outreach_status: 'support_contacted',
      support_contacted_at: new Date().toISOString(),
      support_notes: notes,
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to mark as contacted', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'onboarding_support_contacted',
      entityType: 'onboarding_email_log',
      entityId: id,
      newValues: { notes, outreach_status: 'support_contacted' },
    }), client
  )
}

export async function markOnboardingSupportResolved(
  id: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('onboarding_email_log')
    .update({
      outreach_status: 'resolved',
      support_resolved_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to mark as resolved', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'onboarding_resolved',
      entityType: 'onboarding_email_log',
      entityId: id,
      newValues: { outreach_status: 'resolved' },
    }), client
  )
}

export async function markOnboardingEngaged(
  id: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('onboarding_email_log')
    .update({
      outreach_status: 'engaged',
      support_resolved_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to mark as engaged', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'onboarding_engaged',
      entityType: 'onboarding_email_log',
      entityId: id,
      newValues: { outreach_status: 'engaged' },
    }), client
  )
}

export async function markOnboardingSupportFlagged(
  id: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('onboarding_email_log')
    .update({
      outreach_status: 'support_flagged',
      support_flagged_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) throw new ServiceError('Failed to flag for support', 'DB_ERROR', error)
  await logActivity(
    withActivityActor(actor, {
      action: 'onboarding_flagged',
      entityType: 'onboarding_email_log',
      entityId: id,
      newValues: { outreach_status: 'support_flagged' },
    }), client
  )
}

// UX-8: Resend onboarding email
export async function resendOnboardingEmail(
  studentPhone: string
): Promise<{ success: boolean; error?: string }> {
  const response = await callInternalEdgeFunction('send-onboarding-email', {
    student_phone: studentPhone,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => 'Failed to resend onboarding email')
    return { success: false, error: text }
  }

  const data = await response.json().catch(() => ({}))
  return { success: data?.status === 'sent', error: data?.error }
}
